/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import {
  fetchTaskTemplates,
  createTaskTemplate,
  deleteTaskTemplate,
  updateTaskTemplate,
} from "../services/taskService";
import { useAuth } from "../contexts/AuthContext";
import { FaRegSquare, FaTimes, FaEdit, FaTrash, FaChevronDown, FaChevronUp } from "react-icons/fa";

const EVENT_LABELS = [
  "Upon waking",
  "Before breakfast",
  "During breakfast",
  "After breakfast",
  "Before lunch",
  "During lunch",
  "After lunch",
  "Before dinner",
  "During dinner",
  "After dinner",
  "At bedtime",
];

const defaultSchedule = {
  type: "event", // 'event' | 'time'
  scheduleType: "daily", // 'daily' | 'weekly' | 'custom'
  eventLabel: EVENT_LABELS[0],
  time: "08:00",
  repeat: "daily",
  daysOfWeek: [],
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const InstructionsPage = () => {
  const { activeCareProfile, currentUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [checklistItems, setChecklistItems] = useState([""]);
  const [sched, setSchedule] = useState(defaultSchedule);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadTemplates = async () => {
    if (!activeCareProfile) return;
    setLoading(true);
    try {
      const tpls = await fetchTaskTemplates(activeCareProfile.id);
      setTemplates(tpls);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCareProfile]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setChecklistItems([""]);
    setSchedule(defaultSchedule);
  };

  const startEdit = (tpl) => {
    setEditingTemplate(tpl);
    setTitle(tpl.title);
    setDescription(tpl.description || "");
    setChecklistItems(
      tpl.checklist && tpl.checklist.length ? [...tpl.checklist, ""] : [""]
    );
    setSchedule({
      ...defaultSchedule,
      ...tpl.schedule,
      scheduleType: tpl.schedule.repeat || "daily",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeCareProfile || !currentUser) return;

    const checklist = checklistItems.map((s) => s.trim()).filter(Boolean);

    // Build schedule
    let scheduleObj;
    if (sched.type === "time") {
      scheduleObj = {
        type: "time",
        time: sched.time,
        repeat: sched.scheduleType,
      };
    } else {
      scheduleObj = {
        type: "event",
        eventLabel: sched.eventLabel,
        repeat: sched.scheduleType,
      };
    }
    if (sched.scheduleType === "weekly") {
      scheduleObj = {
        ...scheduleObj,
        repeat: "weekly",
        daysOfWeek: sched.daysOfWeek,
      };
    }

    const payload = {
      title,
      description,
      checklist,
      schedule: scheduleObj,
      reminderEnabled: false,
      createdBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || currentUser.email,
      },
      createdAt: new Date(),
      lastModifiedBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || currentUser.email,
      },
      lastModifiedAt: new Date(),
    };

    try {
      if (editingTemplate) {
        await updateTaskTemplate({
          careProfileId: activeCareProfile.id,
          templateId: editingTemplate.id,
          updates: payload,
        });
      } else {
        await createTaskTemplate({
          careProfileId: activeCareProfile.id,
          templateData: payload,
        });
      }
      resetForm();
      setEditingTemplate(null);
      setShowForm(false);
      await loadTemplates();
    } catch (err) {
      console.error(err);
      alert("Failed to create template: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await deleteTaskTemplate({
        careProfileId: activeCareProfile.id,
        templateId: id,
      });
      await loadTemplates();
    } catch (err) {
      console.error(err);
      alert("Failed to delete: " + err.message);
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Instructions</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-green-600 text-white rounded"
        >
          {showForm ? "Cancel" : "Add New"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 p-4 bg-white rounded shadow border-l-4 border-green-500"
        >
          <div>
            <label className="block font-medium mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full border px-3 py-2 rounded"
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
              className="w-full border px-3 py-2 rounded"
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Checklist</label>
            <div className="space-y-2">
              {checklistItems.map((item, idx) => {
                const isSentinel = idx === checklistItems.length - 1;
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2 group"
                  >
                    <FaRegSquare className="text-gray-400 shrink-0" />
                    <input
                      type="text"
                      value={item}
                      placeholder={isSentinel ? "Add task" : `Task ${idx + 1}`}
                      onChange={(e) => {
                        const val = e.target.value;
                        setChecklistItems((prev) => {
                          const arr = [...prev];
                          arr[idx] = val;
                          if (isSentinel && val.trim() !== "") {
                            arr.push("");
                          }
                          return arr;
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setChecklistItems((prev) => {
                            const arr = [...prev];
                            if (isSentinel && item.trim() !== "") {
                              arr.push("");
                            } else if (!isSentinel && idx === prev.length - 2) {
                              // if second last, ensure sentinel exists
                              if (prev[prev.length - 1].trim() !== "") arr.push("");
                            }
                            return arr;
                          });
                        }
                        if (e.key === "Backspace" && item === "" && !isSentinel) {
                          e.preventDefault();
                          setChecklistItems((prev) => prev.filter((_, i) => i !== idx));
                        }
                      }}
                      className={`flex-1 border-b focus:outline-none px-1 py-1 ${
                        isSentinel ? "italic text-gray-500" : ""
                      }`}
                    />
                    {!isSentinel && (
                      <button
                        type="button"
                        onClick={() => {
                          setChecklistItems((prev) =>
                            prev.filter((_, i) => i !== idx),
                          );
                        }}
                        className="text-gray-400 opacity-0 group-hover:opacity-100 transition"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Schedule simple: daily event label */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">Trigger Type</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    value="event"
                    checked={sched.type === "event"}
                    onChange={() => setSchedule({ ...sched, type: "event" })}
                  />
                  Event
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    value="time"
                    checked={sched.type === "time"}
                    onChange={() => setSchedule({ ...sched, type: "time" })}
                  />
                  Time
                </label>
              </div>
            </div>
            <div>
              <label className="block font-medium mb-1">Schedule Type</label>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="daily"
                  name="scheduleType"
                  value="daily"
                  checked={sched.scheduleType === "daily"}
                  onChange={(e) => setSchedule({ ...sched, scheduleType: e.target.value })}
                  className="form-radio text-green-600"
                />
                <label htmlFor="daily" className="text-sm">Daily</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="weekly"
                  name="scheduleType"
                  value="weekly"
                  checked={sched.scheduleType === "weekly"}
                  onChange={(e) => setSchedule({ ...sched, scheduleType: e.target.value })}
                  className="form-radio text-green-600"
                />
                <label htmlFor="weekly" className="text-sm">Weekly</label>
              </div>
            </div>
            {sched.scheduleType === "weekly" && (
              <div>
                <label className="block font-medium mb-1">Days of Week</label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((day) => (
                    <div key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`day-${day}`}
                        value={day}
                        checked={sched.daysOfWeek.includes(day)}
                        onChange={(e) => {
                          const newDays = [...sched.daysOfWeek];
                          if (e.target.checked) {
                            newDays.push(day);
                          } else {
                            newDays.splice(newDays.indexOf(day), 1);
                          }
                          setSchedule({ ...sched, daysOfWeek: newDays });
                        }}
                        className="form-checkbox text-green-600"
                      />
                      <label htmlFor={`day-${day}`} className="ml-1 text-sm">
                        {day}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sched.type === "event" ? (
              <div>
                <label className="block font-medium mb-1">Event Label</label>
                <select
                  value={sched.eventLabel}
                  onChange={(e) =>
                    setSchedule({ ...sched, eventLabel: e.target.value })
                  }
                  className="w-full border px-3 py-2 rounded"
                >
                  {EVENT_LABELS.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block font-medium mb-1">Time of Day</label>
                <input
                  type="time"
                  value={sched.time}
                  onChange={(e) => setSchedule({ ...sched, time: e.target.value })}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
            )}
            <div>
              <label className="block font-medium mb-1">Repeat</label>
              <select
                value={sched.repeat}
                onChange={(e) =>
                  setSchedule({ ...sched, repeat: e.target.value })
                }
                className="w-full border px-3 py-2 rounded"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
          </div>
          {/* For brevity daysOfWeek not implemented */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => {
                resetForm();
                setEditingTemplate(null);
                setShowForm(false);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              {editingTemplate ? "Update" : "Save"}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {loading ? (
          <p>Loading...</p>
        ) : templates.length === 0 ? (
          <p>No templates yet.</p>
        ) : (
          <div className="space-y-4">
            {templates.map((tpl) => {
              const schedStr =
                tpl.schedule.type === "time"
                  ? `${tpl.schedule.time} · ${tpl.schedule.repeat}`
                  : `${tpl.schedule.eventLabel} · ${tpl.schedule.repeat}`;
              return (
                <div
                  key={tpl.id}
                  className="relative bg-white border-l-4 border-blue-500 p-4 pb-8 rounded shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold">{tpl.title}</h3>
                      <p className="text-sm text-gray-600">{schedStr}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() =>
                          setExpandedId(expandedId === tpl.id ? null : tpl.id)
                        }
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title={expandedId === tpl.id ? "Collapse" : "Expand"}
                      >
                        {expandedId === tpl.id ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                      <button
                        onClick={() => startEdit(tpl)}
                        className="p-1 text-yellow-600 hover:text-yellow-800"
                        title="Edit"
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(tpl.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                  {expandedId === tpl.id && (
                    <div className="mt-3 space-y-2 text-sm text-gray-700">
                      {tpl.description && <p>{tpl.description}</p>}
                      {tpl.checklist && tpl.checklist.length > 0 && (
                        <ul className="list-disc list-inside space-y-1 text-gray-600">
                          {tpl.checklist.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {Array.isArray(tpl.checklist) && tpl.checklist.length > 0 && (
                    <span className="absolute bottom-3 right-3 text-xs text-gray-500">
                      {tpl.checklist.length}
                      {tpl.checklist.length === 1 ? " task" : " tasks"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructionsPage; 